import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useMemo } from 'react'
import usePortalQuery from 'src/Libs/api/hooks/use-portal-query'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import {
  getBatterySavings,
  getBatterySavingsBulk,
  getFirstThreeSixtySavingsEntry,
  getWallboxSavings,
  getWallboxSavingsBulk,
} from '../api/three-sixty-savings-difference-values.api'
import {
  BatterySavingsBulkProps,
  BatterySavingsModel,
  FirstThreeSixtySavingsEntry,
  FirstThreeSixtySavingsEntryModel,
  WallboxSavingsBulkProps,
  WallboxSavingsModel,
} from '../../system-data-handler/types'
import { ThreeSixtySavingsResolution } from '../types'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { useWallboxHardwareByStorage } from 'src/Libs/wallboxes/hooks'

interface ThreeSixtySavingsBulkProps {
  storagesIds: string[]
  isFarm?: boolean
  from?: Moment
  to?: Moment
  isEnabled?: boolean
  resolution?: ThreeSixtySavingsResolution
}

interface FirstThreeSixtySavingsEntryProps {
  storageId: string
  isEnabled: boolean
}

/**
 * Responsible for fetching wallbox savings for 1-n storages.
 */
export const useWallboxSavings = ({
  storagesIds: serialNumbers,
  isFarm = false,
  from,
  to,
  isEnabled,
  resolution,
}: ThreeSixtySavingsBulkProps): WallboxSavingsBulkProps => {
  const authFetch = useAuthFetchWithResult()
  const { data: wallboxes } = useWallboxHardwareByStorage()
  const {
    featureFlags: { isNewEmobiltyRouteEnabled },
    isFeatureFlagsLoading,
  } = useFeatureFlagsContext()
  const shouldUseNewRoute = !isFeatureFlagsLoading && isNewEmobiltyRouteEnabled
  const {
    data: results,
    isLoading,
    error,
    isError,
  } = usePortalQuery<WallboxSavingsModel>({
    queryKey: ['wallboxSavingsBulk', serialNumbers, from, to],
    queryFn: () => {
      if (isFarm) {
        return getWallboxSavingsBulk(authFetch, serialNumbers, shouldUseNewRoute, from?.valueOf(), to?.valueOf(), resolution)
      }
      return getWallboxSavings(authFetch, shouldUseNewRoute, serialNumbers[0], from?.valueOf(), to?.valueOf())
    },
    enabled: isEnabled && serialNumbers.length > 0 && wallboxes?.length > 0,
    retry: 1,
  })

  return useMemo(
    () => ({
      data: {
        time: results?.time ?? 0,
        wallboxSavings: results?.wallboxSavings ?? 0,
        consumedPower: results?.consumedPower ?? 0,
        weightedPrice: results?.weightedPrice ?? 0,
      },
      isLoading,
      errorCode: (error as AuthFetchError)?.response?.data?.errno ?? 0,
      errorMessage: (error as AuthFetchError)?.response?.data?.error ?? '',
      hasError: isError ?? false,
    }),
    [results, isLoading, error, isError]
  )
}

/**
 * Responsible for fetching battery savings for 1-n storages.
 */
export const useBatterySavings = ({
  storagesIds: serialNumbers,
  isFarm = false,
  from,
  to,
  isEnabled,
  resolution,
}: ThreeSixtySavingsBulkProps): BatterySavingsBulkProps => {
  const authFetch = useAuthFetchWithResult()

  const {
    data: results,
    isError,
    isLoading,
  } = usePortalQuery<BatterySavingsModel>({
    queryKey: ['BatterySavingsBulk', serialNumbers, from, to, resolution],
    queryFn: () => {
      if (isFarm) {
        return getBatterySavingsBulk(authFetch, serialNumbers, from?.valueOf(), to?.valueOf(), resolution)
      }
      return getBatterySavings(authFetch, serialNumbers[0], from?.valueOf(), to?.valueOf(), resolution)
    },
    enabled: isEnabled && serialNumbers.length > 0,
    retry: 1,
  })

  return useMemo(
    () => ({
      data: {
        batterySavings: results?.batterySavings ?? 0,
        time: results?.time ?? 0,
        consumedPower: results?.consumedPower ?? 0,
        weightedPrice: results?.weightedPrice ?? 0,
      },
      isLoading,
      hasError: isError ?? false,
    }),
    [results, isLoading, isError]
  )
}

/** this hook provides the first savings entry date wether its Battery or Wallbox and error response */
export const useFirstThreeSixtySavingsEntry = ({ storageId, isEnabled }: FirstThreeSixtySavingsEntryProps): FirstThreeSixtySavingsEntry => {
  const authFetch = useAuthFetchWithResult()
  const { data: results, isLoading } = usePortalQuery<FirstThreeSixtySavingsEntryModel>({
    queryKey: ['firstSavings', storageId],
    queryFn: () => getFirstThreeSixtySavingsEntry(authFetch, storageId),
    enabled: isEnabled,
  })

  return {
    firstSavingsDate: results?.firstSavingsDate?.toString(),
    isLoading,
  }
}
